import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
  styled,
} from "@mui/material";
import { Layer, GeoJSON } from "leaflet";

import { ErrorFormat, deserializeErrors } from "utils/ErrorUtils";

import { UploadIcon } from "components/icons/UploadIcon";
import { UploadDropZone } from "components/upload/UploadDropZone";
import { LoadingButton } from "components/buttons/LoadingButton";
import {
  useStudyAreaTemplateUrl,
  useUploadStudyAreaShape,
} from "hooks/uploadHooks";

interface Props {
  setGeoJSONLayer: (geoJSONLayer: GeoJSON | Layer | null) => void;
  maxUploadSizeMB: number;
  isDisabled: boolean;
}

export function UploadStudyArea(props: Props) {
  const { setGeoJSONLayer, isDisabled, maxUploadSizeMB } = props;
  const { t } = useTranslation();
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [fileUploadErrors, setFileUploadErrors] = useState<ErrorFormat[]>([]);
  const { mutateAsync, isLoading } = useUploadStudyAreaShape();
  const [openDialog, setOpenDialog] = useState(false);
  const { data: url } = useStudyAreaTemplateUrl();
  function closeDialog() {
    setOpenDialog(false);
  }

  async function handleUpload() {
    if (acceptedFiles.length === 0) return;
    const [file] = acceptedFiles;
    await mutateAsync(file, {
      onSuccess: (data) => {
        setGeoJSONLayer(data);
        closeDialog();
      },
      onError: (error) => {
        const errors = deserializeErrors(error);
        setFileUploadErrors(errors);
      },
    });
  }

  return (
    <>
      <Button
        disabled={isDisabled}
        variant="text-icon"
        startIcon={<UploadIcon />}
        onClick={() => setOpenDialog(true)}
      >
        {t("label.uploadAShapefile")}
      </Button>
      <StyledDialog open={openDialog} onClose={closeDialog}>
        <DialogTitle color="primary">
          <UploadIcon />
          {t("label.uploadAShapefile")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("label.uploadZipWithShapefile")}</Typography>
          <ul>
            <li>{t("label.shapefileUploadCondition1")}</li>
            <li>{t("label.shapefileUploadCondition8")}</li>
            <li>
              {t("label.shapefileUploadCondition4", {
                geometryType: t("label.points"),
              })}
            </li>
            <li>{t("label.shapefileUploadCondition6")}</li>
          </ul>
          <UploadDropZone
            maxUploadSizeMB={maxUploadSizeMB}
            setAcceptedFiles={setAcceptedFiles}
            errors={fileUploadErrors}
            resetErrors={() => setFileUploadErrors([])}
          />
          <Link mt={2} href={url} download>
            {t("label.downloadExampleFile")}
          </Link>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant="back-button">
            {t("label.cancel")}
          </Button>
          <LoadingButton
            isLoading={isLoading}
            variant="green-contained"
            disabled={acceptedFiles.length === 0 || isLoading}
            onClick={handleUpload}
          >
            {t("label.upload")}
          </LoadingButton>
        </DialogActions>
      </StyledDialog>
    </>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialogTitle-root {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    svg {
      margin-top: -2px;
    }
  }

  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
  }

  .MuiButton-root {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;
